import { useState } from 'react';
import { toast } from 'react-toastify';

/**
 * Single-shot verifier for direct-lookup fields (BVN, NIN, CAC...).
 * Tracks the exact value that was verified so edits invalidate it, and caps
 * the number of failed attempts when `maxAttempts` is provided.
 */
export const useVerifier = ({ verify, maxAttempts = null, initialAttempts = 0 } = {}) => {
  const [verifiedValue, setVerifiedValue] = useState('');
  const [verifying, setVerifying] = useState(false);
  // Seeded from the agency record's verification_attempts so the count (and lock)
  // survives a reload; in-session failures increment it locally.
  const [attempts, setAttempts] = useState(initialAttempts);

  const locked = maxAttempts != null && attempts >= maxAttempts;
  const attemptsLeft = maxAttempts != null ? Math.max(0, maxAttempts - attempts) : null;

  // `value` is what gets recorded as verified; `payload` is sent to the API.
  const run = async (value, payload) => {
    if (locked) {
      toast.error('Maximum verification attempts reached. Please contact support.');
      return null;
    }

    setVerifying(true);
    const { success, details } = await verify(payload);
    setVerifying(false);

    if (!success) {
      setAttempts((n) => n + 1);
      return null;
    }

    setVerifiedValue(value);
    return details || true;
  };

  const isVerified = (value) => !!verifiedValue && verifiedValue === value;

  return { verifying, attempts, attemptsLeft, locked, run, isVerified, setVerifiedValue };
};
